import React from 'react';
import classNames from 'classnames';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { createUseStyles } from 'react-jss';

import { ThemeProps } from '@styles/theme';
import { useTheme } from '@mui/material/styles';

export interface NavLinkProps {
    to: string;
    children: React.ReactNode;
    onClick?: () => void;
    isText?: boolean;
    title?: string;
    ariaLabel?: string;
    testId?: string;
}

const useStyles = createUseStyles((theme: ThemeProps) => ({
    textWrapper: {
        color: theme.colors.blue_primary,
        fontFamily: theme.font.secondary,
        fontSize: 20,
        lineHeight: 1.4,
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 44,
        padding: `${theme.spacing(1)} ${theme.spacing(5)}`,
        border: '1px solid transparent',
        borderRadius: theme.radius,
        transition: '0.3s',
        [theme.down('wide')]: {
            fontSize: 18,
            padding: `${theme.spacing(0.75)} ${theme.spacing(3.5)}`,
            minHeight: 40,
        },
        [theme.down('desktop')]: {
            fontSize: 16,
            padding: `${theme.spacing(0.5)} ${theme.spacing(2.5)}`,
            minHeight: 36,
        },
        [theme.touch]: {
            minHeight: 44,
        },

        '&:focus': {
            boxShadow: `0 0 4px 0 ${theme.colors.blue_neon}`,
            borderColor: theme.colors.blue_primary,
            outline: 'none',
        },

        '&:active': {
            color: theme.colors.blue_neon_luminating,
            boxShadow: 'none',
        },

        ['&.active, &:hover']: {
            color: theme.colors.blue_neon,
            cursor: 'pointer',
            backgroundColor: 'rgba(9, 237, 245, 0.12)',
            boxShadow: 'none',
            borderColor: 'transparent',
        },
    },
    iconWrapper: {
        color: theme.colors.blue_primary,
        border: '1px solid transparent',
        borderRadius: theme.radius,
        transition: '0.3s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 44,
        minHeight: 44,

        '&:focus': {
            boxShadow: `0 0 4px 0 ${theme.colors.blue_neon}`,
            borderColor: theme.colors.blue_primary,
            outline: 'none',
        },

        '&:active': {
            color: theme.colors.blue_neon_luminating,
            boxShadow: 'none',
        },

        ['&.active, &:hover']: {
            color: theme.colors.blue_neon,
            cursor: 'pointer',
            backgroundColor: 'rgba(9, 237, 245, 0.12)',
            boxShadow: 'none',
            borderColor: 'transparent',
        },
    },
}))

// TODO similar as Link, should be refactored
export const NavLink: React.FC<NavLinkProps> = ({
    to,
    children,
    onClick,
    isText = true,
    title,
    ariaLabel,
    testId,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    return (
        <RouterNavLink
            to={to}
            onClick={onClick}
            title={title}
            aria-label={ariaLabel}
            data-testid={testId}
            className={
                classNames(isText ? styles.textWrapper : styles.iconWrapper)
            }
        >
            {children}
        </RouterNavLink>
    )
};

NavLink.displayName = 'NavLink';
